import { useState } from "react";
import { NavLink, Outlet } from "react-router-dom";
import { LayoutDashboard, Plane, Grid3x3, Crosshair, Map, LogOut, Menu, X, Shield, Eye, FileText } from "lucide-react";
import { useAuth } from "@/context/AuthContext";

const NAV = [
  { to: "/", label: "Dashboard", icon: LayoutDashboard, id: "dashboard" },
  { to: "/flight", label: "Tabular Flight", icon: Plane, id: "flight" },
  { to: "/grid", label: "Tabular Grid", icon: Grid3x3, id: "grid" },
  { to: "/cp", label: "Tabular CP", icon: Crosshair, id: "cp" },
  { to: "/dokumen", label: "Document Progress", icon: FileText, id: "dokumen" },
  { to: "/gis", label: "Peta GIS", icon: Map, id: "gis" },
];

// Build id = cap waktu saat artefak ini dibangun, supaya build yang tersaji di browser bisa
// dipastikan yang terbaru tanpa menebak. Dihitung otomatis oleh scripts/build.sh (WIB, dengan
// detik) lalu diserahkan sebagai --build-arg; tidak ada yang perlu diingat atau dinaikkan manual.
// Detik disembunyikan di sini karena mata hanya butuh pembeda yang terbaca cepat — nilai
// lengkapnya tetap utuh di /api/health untuk membedakan dua build dalam menit yang sama.
// "dev" berarti build dipanggil tanpa skrip (docker compose up --build langsung): sinyal
// skripnya dilewati, bukan bug. Kenapa cap waktu dan bukan hash commit: lihat docs/adr/0001.
const BUILD_ID = process.env.REACT_APP_BUILD_ID || "dev";

const Sidebar = ({ onNavigate }) => {
  const { user, logout, isAdmin } = useAuth();
  return (
    <div className="flex h-full flex-col bg-[#0B0F17] border-r border-border">
      <div className="px-6 pt-7 pb-8">
        <p className="font-mono text-[10px] uppercase tracking-[0.3em] text-sky-400">Progress Report</p>
        <h1 className="font-display text-3xl font-extrabold leading-none mt-2">PEMETAAN 3D<br /><span className="text-slate-400">SEMARANG</span></h1>
      </div>
      <nav className="flex-1 px-3 space-y-1">
        {NAV.map(({ to, label, icon: Icon, id }) => (
          <NavLink key={to} to={to} end={to === "/"} onClick={onNavigate} data-testid={`nav-${id}`}
            className={({ isActive }) => `group flex items-center gap-3 rounded-md px-3 py-2.5 text-sm font-medium transition-colors duration-200 ${isActive ? "bg-sky-500/10 text-sky-300 border-l-2 border-sky-400" : "text-slate-400 hover:bg-white/5 hover:text-slate-100 border-l-2 border-transparent"}`}>
            <Icon className="h-4 w-4" />{label}
          </NavLink>
        ))}
      </nav>
      <div className="m-3 rounded-md border border-border bg-[#131B29] p-4">
        <div className="flex items-center justify-between">
          <p className="truncate text-sm font-semibold" data-testid="sidebar-user-email">{user?.email}</p>
        </div>
        <span data-testid="sidebar-user-role" className={`mt-2 inline-flex items-center gap-1 rounded-full px-2 py-0.5 font-mono text-[10px] uppercase tracking-widest ${isAdmin ? "bg-emerald-500/15 text-emerald-300" : "bg-slate-500/20 text-slate-300"}`}>
          {isAdmin ? <Shield className="h-3 w-3" /> : <Eye className="h-3 w-3" />}{user?.role}
        </span>
        <button onClick={logout} data-testid="logout-button" className="mt-4 flex w-full items-center justify-center gap-2 rounded-md border border-border py-2 text-xs text-slate-300 transition-colors hover:bg-rose-500/10 hover:text-rose-300 hover:border-rose-500/40">
          <LogOut className="h-3.5 w-3.5" /> Keluar
        </button>
      </div>
      {/* Kaki sidebar: build id. `title` memuat nilai penuh (dengan detik dan WIB) supaya
          barisnya tetap ringkas tapi detailnya bisa dilihat saat hover. */}
      <p data-testid="build-id" title={`Build ID ${BUILD_ID} WIB`}
        className="px-6 pb-4 font-mono text-[10px] text-slate-500">
        build {BUILD_ID.slice(0, 16)} · WIB
      </p>
    </div>
  );
};

export default function Layout() {
  const [open, setOpen] = useState(false);
  return (
    <div className="min-h-screen">
      <aside className="fixed inset-y-0 left-0 z-[1100] hidden w-64 lg:block"><Sidebar /></aside>
      {open && (
        <div className="fixed inset-0 z-[1200] lg:hidden">
          <div className="absolute inset-0 bg-black/60" onClick={() => setOpen(false)} />
          <aside className="absolute inset-y-0 left-0 w-64"><Sidebar onNavigate={() => setOpen(false)} /></aside>
        </div>
      )}
      <header className="sticky top-0 z-[1000] flex items-center justify-between border-b border-border bg-[#0B0F17]/95 backdrop-blur px-4 py-3 lg:hidden">
        <span className="font-display text-xl font-bold">PEMETAAN 3D SEMARANG</span>
        <button data-testid="mobile-menu-button" onClick={() => setOpen(!open)} className="p-2">{open ? <X /> : <Menu />}</button>
      </header>
      <main className="lg:pl-64"><Outlet /></main>
    </div>
  );
}
